import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getDashboard, approveDraft, rejectDraft, updateActionStatus, seedData, getExecutiveSummary } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { formatDate, formatDateTime, getStatusColor, truncateText } from '../lib/utils';
import { 
  AlertCircle, CheckCircle, Clock, FileText, Users, 
  Target, Briefcase, Car, MessageSquare, Play, Calendar, AlertTriangle,
  TrendingUp, Megaphone, Globe, Mic, Eye, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const DashboardPage = () => {
  const [dashboard, setDashboard] = useState(null);
  const [executiveSummary, setExecutiveSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const isFounder = user?.role === 'Founder';

  const fetchDashboard = async () => {
    try {
      const response = await getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchExecutiveSummary = async () => {
    try {
      const response = await getExecutiveSummary();
      setExecutiveSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch executive summary:', error);
    }
  };

  useEffect(() => {
    fetchDashboard();
    fetchExecutiveSummary();
  }, []);

  const handleApproveDraft = async (id) => {
    try {
      await approveDraft(id);
      toast.success('Draft approved');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to approve draft');
    }
  };

  const handleRejectDraft = async (id) => {
    try {
      await rejectDraft(id);
      toast.success('Draft rejected');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to reject draft');
    }
  };

  const handleCompleteAction = async (id) => {
    try {
      await updateActionStatus(id, 'Completed');
      toast.success('Action marked complete');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to update action');
    }
  };

  const handleSeedData = async () => {
    try {
      await seedData();
      toast.success('Initial data seeded');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to seed data');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-500 font-inter">Loading dashboard...</div>
      </div>
    );
  }

  const counts = dashboard?.counts || {};

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Stats Grid - Mobile Responsive */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 font-inter uppercase tracking-wider">Threads</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-zinc-900">{counts.total_threads || 0}</p>
              </div>
              <Target className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 font-inter uppercase tracking-wider">Evidence</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-zinc-900">{counts.total_evidence_records || 0}</p>
              </div>
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 font-inter uppercase tracking-wider">Actions</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-zinc-900">{counts.total_action_items || 0}</p>
              </div>
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 font-inter uppercase tracking-wider">Funnels</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-zinc-900">{counts.total_funnel_items || 0}</p>
              </div>
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 font-inter uppercase tracking-wider">Deals</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-zinc-900">{counts.total_deals || 0}</p>
              </div>
              <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="border border-zinc-200 shadow-none rounded-sm">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500 font-inter uppercase tracking-wider">Vehicles</p>
                <p className="text-xl sm:text-2xl font-bold font-mono text-zinc-900">{counts.total_vehicles || 0}</p>
              </div>
              <Car className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alert Cards - Mobile Responsive */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card className={`border shadow-none rounded-sm ${counts.pending_approvals > 0 ? 'border-amber-300 bg-amber-50' : 'border-zinc-200'}`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-1 sm:gap-2">
              <MessageSquare className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${counts.pending_approvals > 0 ? 'text-amber-600' : 'text-zinc-400'}`} />
              <span className="font-medium font-inter text-xs sm:text-sm truncate">Pending</span>
              <Badge className={`ml-auto flex-shrink-0 text-xs ${counts.pending_approvals > 0 ? 'bg-amber-500' : 'bg-zinc-400'}`}>
                {counts.pending_approvals || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className={`border shadow-none rounded-sm ${counts.overdue_actions > 0 ? 'border-red-300 bg-red-50' : 'border-zinc-200'}`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-1 sm:gap-2">
              <AlertCircle className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${counts.overdue_actions > 0 ? 'text-red-600' : 'text-zinc-400'}`} />
              <span className="font-medium font-inter text-xs sm:text-sm truncate">Overdue</span>
              <Badge className={`ml-auto flex-shrink-0 text-xs ${counts.overdue_actions > 0 ? 'bg-red-500' : 'bg-zinc-400'}`}>
                {counts.overdue_actions || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className={`border shadow-none rounded-sm ${counts.stalled_funnels > 0 ? 'border-orange-300 bg-orange-50' : 'border-zinc-200'}`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-1 sm:gap-2">
              <Clock className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${counts.stalled_funnels > 0 ? 'text-orange-600' : 'text-zinc-400'}`} />
              <span className="font-medium font-inter text-xs sm:text-sm truncate">Stalled</span>
              <Badge className={`ml-auto flex-shrink-0 text-xs ${counts.stalled_funnels > 0 ? 'bg-orange-500' : 'bg-zinc-400'}`}>
                {counts.stalled_funnels || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className={`border shadow-none rounded-sm ${counts.calendar_conflicts > 0 ? 'border-purple-300 bg-purple-50' : 'border-zinc-200'}`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-1 sm:gap-2">
              <AlertTriangle className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 ${counts.calendar_conflicts > 0 ? 'text-purple-600' : 'text-zinc-400'}`} />
              <span className="font-medium font-inter text-xs sm:text-sm truncate">Conflicts</span>
              <Badge className={`ml-auto flex-shrink-0 text-xs ${counts.calendar_conflicts > 0 ? 'bg-purple-500' : 'bg-zinc-400'}`}>
                {counts.calendar_conflicts || 0}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* What Happened Today? - Executive Summary (Advisory Only) */}
      {executiveSummary && (
        <Card className="border border-blue-200 bg-blue-50/30 shadow-none rounded-sm" data-testid="executive-summary-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-manrope text-base flex items-center gap-2">
                <Eye className="w-4 h-4 text-blue-600" />
                What Happened Today?
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs bg-white">
                  <Info className="w-3 h-3 mr-1" />
                  Advisory Only
                </Badge>
                <span className="text-xs text-zinc-500 font-inter">{executiveSummary.date}</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-4">
              {/* Pending Intakes */}
              <div className={`p-2 rounded-sm text-center ${executiveSummary.summary?.pending_intakes > 0 ? 'bg-amber-100 border border-amber-200' : 'bg-white border border-zinc-200'}`}>
                <Mic className={`w-4 h-4 mx-auto mb-1 ${executiveSummary.summary?.pending_intakes > 0 ? 'text-amber-600' : 'text-zinc-400'}`} />
                <p className="text-lg font-bold font-mono">{executiveSummary.summary?.pending_intakes || 0}</p>
                <p className="text-xs text-zinc-500 font-inter">Intakes</p>
              </div>
              
              {/* Evidence Captured */}
              <div className="bg-white border border-zinc-200 p-2 rounded-sm text-center">
                <FileText className="w-4 h-4 mx-auto mb-1 text-zinc-400" />
                <p className="text-lg font-bold font-mono">{executiveSummary.summary?.evidence_captured || 0}</p>
                <p className="text-xs text-zinc-500 font-inter">Evidence</p>
              </div>
              
              {/* Financial Events */}
              <div className={`p-2 rounded-sm text-center ${executiveSummary.summary?.financial_events > 0 ? 'bg-emerald-100 border border-emerald-200' : 'bg-white border border-zinc-200'}`}>
                <TrendingUp className={`w-4 h-4 mx-auto mb-1 ${executiveSummary.summary?.financial_events > 0 ? 'text-emerald-600' : 'text-zinc-400'}`} />
                <p className="text-lg font-bold font-mono">{executiveSummary.summary?.financial_events || 0}</p>
                <p className="text-xs text-zinc-500 font-inter">Financial</p>
              </div>
              
              {/* Campaign Signals */}
              <div className={`p-2 rounded-sm text-center ${executiveSummary.summary?.campaign_signals > 0 ? 'bg-purple-100 border border-purple-200' : 'bg-white border border-zinc-200'}`}>
                <Megaphone className={`w-4 h-4 mx-auto mb-1 ${executiveSummary.summary?.campaign_signals > 0 ? 'text-purple-600' : 'text-zinc-400'}`} />
                <p className="text-lg font-bold font-mono">{executiveSummary.summary?.campaign_signals || 0}</p>
                <p className="text-xs text-zinc-500 font-inter">Campaign</p>
              </div>
              
              {/* Narrative Signals */}
              <div className={`p-2 rounded-sm text-center ${executiveSummary.summary?.narrative_signals > 0 ? 'bg-indigo-100 border border-indigo-200' : 'bg-white border border-zinc-200'}`}>
                <Globe className={`w-4 h-4 mx-auto mb-1 ${executiveSummary.summary?.narrative_signals > 0 ? 'text-indigo-600' : 'text-zinc-400'}`} />
                <p className="text-lg font-bold font-mono">{executiveSummary.summary?.narrative_signals || 0}</p>
                <p className="text-xs text-zinc-500 font-inter">Narrative</p>
              </div>
              
              {/* Active Threads */}
              <div className="bg-white border border-zinc-200 p-2 rounded-sm text-center">
                <Target className="w-4 h-4 mx-auto mb-1 text-zinc-400" />
                <p className="text-lg font-bold font-mono">{executiveSummary.summary?.active_threads || 0}</p>
                <p className="text-xs text-zinc-500 font-inter">Threads</p>
              </div>
              
              {/* Pending Approvals */}
              <div className={`p-2 rounded-sm text-center ${executiveSummary.summary?.pending_approvals > 0 ? 'bg-amber-100 border border-amber-200' : 'bg-white border border-zinc-200'}`}>
                <MessageSquare className={`w-4 h-4 mx-auto mb-1 ${executiveSummary.summary?.pending_approvals > 0 ? 'text-amber-600' : 'text-zinc-400'}`} />
                <p className="text-lg font-bold font-mono">{executiveSummary.summary?.pending_approvals || 0}</p>
                <p className="text-xs text-zinc-500 font-inter">Approvals</p>
              </div>
              
              {/* Overdue Actions */}
              <div className={`p-2 rounded-sm text-center ${executiveSummary.summary?.overdue_actions > 0 ? 'bg-red-100 border border-red-200' : 'bg-white border border-zinc-200'}`}>
                <AlertCircle className={`w-4 h-4 mx-auto mb-1 ${executiveSummary.summary?.overdue_actions > 0 ? 'text-red-600' : 'text-zinc-400'}`} />
                <p className="text-lg font-bold font-mono">{executiveSummary.summary?.overdue_actions || 0}</p>
                <p className="text-xs text-zinc-500 font-inter">Overdue</p>
              </div>
            </div>
            
            {/* Advisory Notice */}
            <div className="bg-white border border-zinc-200 rounded-sm p-3 text-center">
              <p className="text-xs text-zinc-500 font-inter">
                {executiveSummary.advisory || 'This is a read-only situational awareness view. No actions or drafts are auto-generated.'}
              </p>
              {executiveSummary.summary?.pending_intakes > 0 && (
                <Link to="/intake/pending">
                  <Button variant="outline" size="sm" className="mt-2 h-7 text-xs">
                    Review Pending Intakes ({executiveSummary.summary.pending_intakes})
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendar Section - Upcoming Booked Time */}
      {dashboard?.calendar && (
        <Card className="border border-zinc-200 shadow-none rounded-sm" data-testid="calendar-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-manrope text-base flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Upcoming Booked Time (48h)
              </CardTitle>
              {!dashboard.calendar.connected && (
                <Link to="/settings">
                  <Button variant="outline" size="sm" className="h-7 text-xs">Connect Calendar</Button>
                </Link>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {dashboard.calendar.connected ? (
              <div className="space-y-3">
                {dashboard.calendar.conflicts?.length > 0 && (
                  <div className="bg-purple-50 border border-purple-200 rounded-sm p-3 mb-3">
                    <p className="text-sm font-medium text-purple-800 mb-2">⚠️ Scheduling Conflicts</p>
                    {dashboard.calendar.conflicts.map((conflict, idx) => (
                      <p key={idx} className="text-xs text-purple-700">{conflict.message}</p>
                    ))}
                  </div>
                )}
                {dashboard.calendar.events?.length > 0 ? (
                  dashboard.calendar.events.slice(0, 5).map((event) => (
                    <div key={event.id} className="border border-zinc-100 p-3 rounded-sm">
                      <p className="font-medium font-inter text-sm">{event.summary}</p>
                      <p className="text-xs text-zinc-500 font-inter">
                        {new Date(event.start).toLocaleString()} 
                        {event.attendees_count > 0 && ` • ${event.attendees_count} attendees`}
                      </p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-zinc-500 font-inter">No upcoming events</p>
                )}
              </div>
            ) : (
              <p className="text-sm text-zinc-500 font-inter">
                Calendar not connected. <Link to="/settings" className="text-blue-600 hover:underline">Connect in Settings</Link>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Approvals */}
        <Card className="border border-zinc-200 shadow-none rounded-sm" data-testid="pending-approvals-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-manrope text-base flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Awaiting Approval
              {!isFounder && <Badge variant="outline" className="text-xs">View Only</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.pending_approvals?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.pending_approvals.slice(0, 5).map((draft) => (
                  <div key={draft.id} className="border border-zinc-100 p-3 rounded-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium font-inter text-sm truncate">{draft.recipient}</p>
                        <p className="text-xs text-zinc-500 font-inter">{draft.channel} • {truncateText(draft.body, 60)}</p>
                      </div>
                      {isFounder && (
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 px-2 text-xs rounded-sm"
                            onClick={() => handleApproveDraft(draft.id)}
                            data-testid={`approve-draft-${draft.id}`}
                          >
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="h-7 px-2 text-xs rounded-sm text-red-600"
                            onClick={() => handleRejectDraft(draft.id)}
                          >
                            Reject
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-500 font-inter">No pending approvals</p>
            )}
          </CardContent>
        </Card>

        {/* Overdue Actions */}
        <Card className="border border-zinc-200 shadow-none rounded-sm" data-testid="overdue-actions-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-manrope text-base flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              Overdue Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            {dashboard?.overdue_actions?.length > 0 ? (
              <div className="space-y-3">
                {dashboard.overdue_actions.slice(0, 5).map((action) => (
                  <div key={action.id} className="border border-red-100 bg-red-50/50 p-3 rounded-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium font-inter text-sm">{truncateText(action.description, 50)}</p>
                        <p className="text-xs text-zinc-500 font-inter">
                          Due: {formatDate(action.due_date)} • 
                          <Badge className={`ml-1 text-xs ${getStatusColor(action.priority)}`}>{action.priority}</Badge>
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 px-2 text-xs rounded-sm"
                        onClick={() => handleCompleteAction(action.id)}
                        data-testid={`complete-action-${action.id}`}
                      >
                        Done
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-500 font-inter">No overdue actions</p>
            )}
          </CardContent>
        </Card>

        {/* Active Threads */}
        <Card className="border border-zinc-200 shadow-none rounded-sm" data-testid="active-threads-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-manrope text-base flex items-center gap-2">
                <Target className="w-4 h-4" />
                Active Threads
              </CardTitle>
              <Link to="/threads">
                <Button variant="ghost" size="sm" className="h-7 text-xs">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {dashboard?.active_threads?.length > 0 ? (
              <div className="space-y-2">
                {dashboard.active_threads.slice(0, 5).map((thread) => (
                  <Link key={thread.id} to={`/threads/${thread.id}`}>
                    <div className="border border-zinc-100 p-3 rounded-sm hover:bg-zinc-50 transition-colors cursor-pointer">
                      <p className="font-medium font-inter text-sm">{thread.name}</p>
                      <p className="text-xs text-zinc-500 font-inter">{truncateText(thread.objective, 60)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-zinc-500 font-inter mb-2">No active threads yet</p>
                <Button variant="outline" size="sm" onClick={handleSeedData} data-testid="seed-data-btn">
                  <Play className="w-3 h-3 mr-1" /> Seed Initial Data
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stalled Funnels */}
        <Card className="border border-zinc-200 shadow-none rounded-sm" data-testid="stalled-funnels-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-manrope text-base flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Stalled Funnels
              </CardTitle>
              <Link to="/funnels">
                <Button variant="ghost" size="sm" className="h-7 text-xs">View All</Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {dashboard?.stalled_funnels?.length > 0 ? (
              <div className="space-y-2">
                {dashboard.stalled_funnels.slice(0, 5).map((funnel) => (
                  <div key={funnel.id} className="border border-orange-100 bg-orange-50/50 p-3 rounded-sm">
                    <p className="font-medium font-inter text-sm">{funnel.party}</p>
                    <p className="text-xs text-zinc-500 font-inter">
                      {funnel.funnel_type} • {funnel.stage} • Next action: {formatDate(funnel.next_action_date)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-500 font-inter">No stalled funnels</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
